// A companion: its design, where it is, what shape and pose it has, and how it's drawn.
// Every companion on screen is one of these; `cast` lists them all.

import { clockNow } from './clock.js';
import { env } from './core.js';
import { buildCompanion, shapesOf } from './rig.js';
import { blit, bounds } from './pixels.js';
import { lookFor, evilLook } from './skins.js';
import { chutePixels, thinkPixels, itemAt } from './props.js';
import { hatHeight } from './outfits.js';
import { pillWallX } from './world.js';

export const cast = [];

// tilt is only used by the old jetpack, kept for comparison; everything else turns with rot.
const restPose = () => ({ sit: false, tilt: 0, legs: 'stand', armL: 'side', armR: 'side', eyes: 'center', variant: null,
  blink: false, glasses: false, breath: false, capDown: false, pack: null, flame: 0, hold: null, holdArm: 'R', flying: false,
  nod: 0,      // nod: how far its head is lowered at the neck, 0..1 (a sheep grazing; needs "neck" in its side view)
  pace: 1,     // pace: how fast its idle animation ("cycle") runs, 1 as designed (a dozing Blaze's rods turn slower)
  tail: 0,     // tail: how much further its tail turns, in radians (a wag; needs "tail" in its side view)
  armsFront: false,   // armsFront: both block arms drawn over the body (fists on its chest)
  grip: null, wrist: 0 });   // grip: an item in its block hand that turns with the arm (a sword), wrist: turned that much further

export function makeCompanion(design, s) {
  const shapes = shapesOf(design);
  return {
    design, shapes, gen: 0,
    look: lookFor(design),                                  // its colors: the design in a skin
    outfit: { body: 'none', head: 'none' },
    x: 0, y: 0, s, jump: 0, bob: 0, alpha: 1, tint: null,
    sink: 0,                                            // how far it's dug into the ground, 0..1 (the Warden)
    // rot: a smooth rotation of the whole drawing (radians, clockwise), around 'feet', 'top' or 'center'.
    rot: 0, rotAround: 'feet',
    view: 'front', face: 1, shape: { ...shapes.normal }, target: { ...shapes.normal, view: 'front', face: 1 }, nextMorph: 0,
    pose: restPose(),
    anchor: null, dest: null, moving: false, idle: false, lost: false,
    atHome: false, think: false, typing: false, sleeping: false, held: false,
    evil: null,                                         // while it's evil: { until, chased } (fights.js)
    chute: 0, bbox: null,
    // The parachute: 'match' (the color of its skin) or a skin's key, in one of CHUTE_PATTERNS.
    parachute: { color: 'match', pattern: 'plain' }, rig: null, babies: [], chair: null, bed: null,
  };
}

// ---------- shape changes ----------
// The shape moves one pixel step at a time toward its target, so it reshapes smoothly. A
// change between front and side view passes through the turned width.
export function setShape(me, name, view = 'front', face = me.face) {
  me.target = { ...(me.shapes[name] || me.shapes.normal), view, face };
}
export function snapShape(me) {
  me.view = me.target.view; me.face = me.target.face;
  me.shape = { bw: me.target.bw, bh: me.target.bh, leg: me.target.leg };
}
// An instant squash or stretch; the morph brings it back by itself.
export function pulse(me, name) { me.shape = { ...(me.shapes[name] || me.shapes.normal) }; me.nextMorph = clockNow() + 90; }

const step = (v, to) => (v < to ? v + 1 : v > to ? v - 1 : v);
export function morph(me, now) {
  if (now < me.nextMorph) return;
  me.nextMorph = now + 45;
  const t = me.target, sh = me.shape, TURN = me.shapes.side.bw;
  // A front drawing that faces a way ("faces": the dragon's 3/4 view) turns around like a side view.
  const turnsAround = t.face !== me.face && (me.view === 'side' || me.design.body.faces === 'right');
  if (t.view !== me.view) {
    sh.bh = step(sh.bh, t.bh); sh.leg = step(sh.leg, t.leg);
    if (me.view === 'front') { if (sh.bw > TURN || (turnsAround && sh.bw > TURN - 3)) { sh.bw--; return; } me.view = 'side'; me.face = t.face; return; }
    // Its own side drawing narrows before it turns to the front, so a side view longer than its
    // front (a cat's long body) never shows its face stretched wide on the way.
    if (me.design.body.side?.art) { if (sh.bw > t.bw) { sh.bw--; return; } me.view = 'front'; return; }
    if (sh.bw < TURN) { sh.bw++; return; }
    me.view = 'front'; return;
  }
  if (turnsAround) {                                         // turning around: narrow, flip, widen
    if (sh.bw > TURN - 3) { sh.bw--; return; }
    me.face = t.face;
  }
  sh.bw = step(sh.bw, t.bw); sh.bh = step(sh.bh, t.bh); sh.leg = step(sh.leg, t.leg);
}

// ---------- resting poses ----------
// lean: turned a little, its back against the wall, on longer legs with arms folded. The whole
// drawing turns smoothly around its feet until the top of its back touches the wall.
export const LEAN_ROT = 0.15;

export function resetPose(me) { Object.assign(me.pose, restPose()); me.rot = 0; me.rotAround = 'feet'; }

// A host can say a surface is only for standing (`sit: false`: a website's line of text, where
// legs hanging over the edge, or a lean's turned feet, would cover the letters under it).
// Companions stand there as they do on the floor, also when they doze off.
export const standOnly = spot => (spot.base || spot.ledge)?.geom?.()?.sit === false;

export function applyRest(me, spot) {
  if ((spot.rest === 'sit' || spot.rest === 'lounge') && standOnly(spot)) spot.rest = 'stand';
  const p = me.pose, keepGlasses = p.glasses && spot.rest === 'lean';
  resetPose(me);
  p.glasses = keepGlasses;
  switch (spot.rest) {
    case 'sit': p.sit = true; setShape(me, 'normal'); break;
    case 'lounge': Object.assign(p, { sit: true, armR: 'head', eyes: 'closed' }); setShape(me, 'normal'); break;
    case 'lean': {
      const right = spot.wallSide === 'R';
      Object.assign(p, { armR: 'crossed', armL: 'none' });   // the back arm is against the wall
      me.rot = right ? LEAN_ROT : -LEAN_ROT;
      setShape(me, 'lean', 'side', right ? -1 : 1);          // facing away from the wall
      break;
    }
    case 'chair': Object.assign(p, { sit: true, armR: 'head', glasses: true }); setShape(me, 'normal'); break;
    case 'bed': Object.assign(p, { sit: true, legs: 'none', eyes: 'sleep', capDown: true }); setShape(me, 'lie'); break;
    // hang: by its feet under a surface (an underside: world.js), upside down, its feet at the spot.
    case 'hang': me.rot = Math.PI; setShape(me, 'normal', 'front'); break;
    default: setShape(me, 'normal');
  }
}

// Hanging, it lets go: upright where its body was, with nothing under it (it falls or flies next).
export function letGo(me) {
  if (me.anchor?.rest !== 'hang') return;
  me.anchor = null; me.rot = 0; me.rotAround = 'feet';
  me.y += (me.shape.bh + me.shape.leg) * me.s;
}

// Its scale (screen pixels per pixel of its drawing): the screen's size made bigger or smaller by
// the owner's size for this companion (SIZES), on a spot that may be smaller still (text).
export const SIZES = { '-2': 'Tiny', '-1': 'Small', 0: 'Normal', 1: 'Big', 2: 'Huge' };
export const scaleOf = me => Math.max(Math.min(env.S, 2), env.S + Math.round((me.size || 0) * env.unit));   // a smaller one stays at least 2
export const spotScale = (me, spot) => Math.min(scaleOf(me), spot.ledge.geom()?.s ?? scaleOf(me));
// A host can say how much room a surface has above it (`room`, in pixels: a website's line of text
// above a heading). A companion only takes a spot it fits under, so its head never covers that,
// nor the hat it wears (its own, its skin's, or the nightcap while it sleeps).
export const heightOf = (me, s = me.s) => (me.shapes.normal.bh + me.shapes.normal.leg + 2 + hatAbove(me)) * s;
function hatAbove(me) {
  const head = me.outfit.head !== 'none' ? me.outfit.head : me.look.wear?.head;
  return head ? Math.max(0, hatHeight(head, me.pose.capDown) - (me.design.body.head?.top || 0)) : 0;
}
export const fits = (me, ledge, s = spotScale(me, { ledge })) => { const room = ledge.geom()?.room; return room == null || room >= heightOf(me, s); };

// How far it reaches out to either side of its middle, in its pixels (arms, claws, wings, splayed
// legs), standing or sitting, seen from the front; worked out once per design. A host can ask for
// the whole body to stay between a surface's ends (`inside: true`: right after the last word of a
// line of text), where the usual margin would let a wide one cover the word.
const reaches = new WeakMap();
export function reachOf(me) {
  let reach = reaches.get(me.design);
  if (reach === undefined) {
    reach = 0;
    for (const sit of [false, true]) {
      const pose = { ...restPose(), sit, ...me.shapes.normal, view: 'front', face: 1, outfit: { body: 'none', head: 'none' } };
      const b = bounds(buildCompanion(me.design, pose, 0).px);
      reach = Math.max(reach, -b.c0, b.c1 + 1);
    }
    reaches.set(me.design, reach);
  }
  return reach;
}
// The margin it keeps from the ends of such a surface, in screen pixels at scale s (elsewhere 9 * s).
export const insideMargin = (me, s) => Math.max(9, reachOf(me) + 1) * s;
// A surface can be a seat at one end rather than a stretch to walk along (`at: 'x1'` or `'x2'`: right
// after the last word of a line). Where its middle goes there, keeping its margin; null elsewhere.
export function seatX(me, g, s) {
  if (g.at !== 'x1' && g.at !== 'x2') return null;
  const half = g.inside ? insideMargin(me, s) : 9 * s;
  return g.at === 'x2' ? g.x2 - half : g.x1 + half;
}

// Where the lean puts the top corner of its back, relative to its feet, after turning.
function leanCorner(me, s) {
  const sh = me.shapes.lean, half = Math.floor(sh.bw / 2), top = -(sh.leg + sh.bh);
  const cos = Math.cos(LEAN_ROT), sin = Math.sin(LEAN_ROT);
  return { x: half * s * cos - top * s * sin, y: half * s * sin + top * s * cos };
}

export function spotPoint(me, spot) {
  const g = spot.ledge.geom();
  if (!g) return null;
  if (spot.rest === 'lean') {
    const s = spotScale(me, spot), right = spot.wallSide === 'R', k = leanCorner(me, s);
    const wallX = g.pill ? pillWallX(g.pill, spot.wallSide, g.y + k.y) : g.wallX;
    const x = right ? wallX - k.x : wallX + k.x;
    const reach = 7 * s;                                                 // how far it stands out from the wall
    if (right ? x - reach < g.x1 - 1 : x + reach > g.x2 + 1) return null;   // no room to stand there any more
    return { x, y: g.y };
  }
  const x = g.x1 + spot.dx;
  if (x < g.x1 - 2 || x > g.x2 + 2) return null;                       // the surface shrank away under it
  return { x, y: g.y };
}

// Where a pixel of its drawing ([col, row] in the art it's drawn with now, front or side view)
// is on screen, at the pixel's middle: it follows the shape, sitting or hovering, jumps and bobs,
// and the mirroring when it faces left. For abilities that shoot or breathe from its mouth.
export function pointOf(me, [col, row]) {
  const body = me.design.body, side = me.view === 'side' && !!body.side, art = side ? body.side.art : body.art;
  const sh = me.shape, W = art[0].length, H = art.length;
  const bottom = me.pose.sit ? -1 : -(sh.leg + 1), top = bottom - sh.bh + 1;
  const c = -Math.floor(sh.bw / 2) + (col + 0.5) * sh.bw / W;
  const flip = me.face < 0 && (side || body.faces === 'right');
  return { x: me.x + (flip ? -c : c) * me.s, y: me.y - me.jump + me.bob + hoverBob(me, clockNow()) + (top + (row + 0.5) * sh.bh / H) * me.s };
}

// Where its hand is on screen, at the hand pixel's middle, as last drawn: it follows jumps and
// bobs and the turn of the whole drawing (rot, as drawTurned does it). For something held that's
// drawn on its own (the grappling hook's gun).
export function handPoint(me, arm) {
  const rig = me.rig, h = rig?.hand[arm] || [arm === 'R' ? 7 : -8, -3], s = me.s;
  const X = Math.round(me.x), Y = Math.round(me.y - me.jump + me.bob + hoverBob(me, clockNow()));
  const x = X + (h[0] + 0.5) * s, y = Y + (h[1] + 0.5) * s;
  if (Math.abs(me.rot) < 0.001 || !rig) return { x, y };
  const py = me.rotAround === 'top' ? Y + rig.top * s : me.rotAround === 'center' ? Y + ((rig.top + rig.bottom + 1) * s) / 2 : Y;
  const cos = Math.cos(me.rot), sin = Math.sin(me.rot);
  return { x: X + (x - X) * cos - (y - py) * sin, y: py + (x - X) * sin + (y - py) * cos };
}

// A host can hold what a drawing does by itself still (reduced motion, or a visitor's "Still"):
// legs swinging as it sits, a light that blinks, a floater's bob. They're drawn as at one moment.
let idleHeld = false;
export function holdIdle(on) { idleHeld = !!on; }

// A floating mob's own gentle bob ("bob": [pixels, ms] in its body: a bee hovering in place), on
// whenever it's up in the air by itself: not while it sits, sleeps, travels, dances or is carried.
export function hoverBob(me, now) {
  const b = me.design.body.bob;
  if (!b || idleHeld || me.pose.sit || me.sleeping || me.moving || me.dancing || me.held) return 0;
  return Math.round(Math.sin(now * 2 * Math.PI / b[1]) * b[0]) * me.s;
}

// Just above its head (the head its design marks, at the top of what's drawn), or null.
export function aboveHead(me) {
  const body = me.design.body, head = (me.view === 'side' && body.side?.head) || body.head;
  if (!head || !me.bbox || body.kind !== 'art') return null;
  return { x: pointOf(me, [(head.cols[0] + head.cols[1]) / 2, head.top]).x, y: me.bbox.t };
}

// ---------- drawing ----------
const off = document.createElement('canvas'), offCtx = off.getContext('2d');

export function drawCompanion(me, now) {
  me.bbox = null;
  if (me.alpha <= 0) return;
  const ctx = env.ctx, s = me.s, look = me.evil ? evilLook(me.design, me.look) : me.look;   // evil: glowing eyes (fights.js)
  const eyes = me.pose.eyes === 'center' && me.gaze ? me.gaze : me.pose.eyes;   // it watches the pointer when it's close
  // What the skin wears: its hat when no other hat is on, its sunglasses while awake.
  const wear = look.wear;
  const outfit = wear?.head && me.outfit.head === 'none' ? { ...me.outfit, head: wear.head } : me.outfit;
  const glasses = me.pose.glasses || (!!wear?.glasses && me.pose.eyes !== 'sleep');
  const rig = buildCompanion(look.design || me.design, { ...me.pose, eyes, glasses, outfit, eyeColors: look.eyes, extra: look.extra, view: me.view, face: me.face, ...me.shape }, idleHeld ? 0 : now);   // (a skin with drawings of its own: an outfit)
  // Dug into the ground it's drawn lower and cut off at the surface.
  const sunk = me.sink > 0 ? me.sink * (me.shape.bh + me.shape.leg) * s : 0;
  const X = me.x, Y = me.y - me.jump + me.bob + hoverBob(me, now) + sunk;
  if (sunk) { ctx.save(); ctx.beginPath(); ctx.rect(-1e5, -1e5, 2e5, me.y + 1e5); ctx.clip(); }
  ctx.globalAlpha = me.alpha;
  if (me.chute > 0) {
    const { color, pattern } = me.parachute;
    const ch = chutePixels(me.chute, rig.hatTop, rig.hand.L, rig.hand.R, pattern);
    blit(ch.strings, X, Y, s, now, null);
    blit(ch.canopy, X, Y, s, now, color === 'match' ? look : lookFor(me.design, color));
  }
  // Everything that turns with the body: its layers and the item in its hand, in front.
  const layers = [...(rig.layers || [{ px: rig.px }])];
  if (me.pose.hold) {
    const arm = me.pose.holdArm || 'R';
    const facing = me.view === 'side' ? me.face : arm === 'L' ? -1 : 1;
    layers.push({ px: itemAt(me.pose.hold, rig.hand[arm], facing) });
  }
  if (Math.abs(me.rot) < 0.001) paintLayers(ctx, layers, X, Y, s, now, look, me.tint);
  else drawTurned(layers, X, Y, s, now, look, me, rig);
  if (me.think) blit(thinkPixels(now, rig.hatTop), X, Y, s, now, look);
  ctx.globalAlpha = 1;
  if (sunk) ctx.restore();
  const b = bounds(rig.px);
  me.bbox = { l: X + b.c0 * s, r: X + (b.c1 + 1) * s, t: Y + b.r0 * s, b: Y + (b.r1 + 1) * s };
  if (Math.cos(me.rot) < 0) me.bbox = turnedBox(me.bbox, me, rig, X, Y);
  me.rig = rig;
}

// Turned more than a quarter turn (hanging upside down), its box is where the turned drawing is
// (turned as drawTurned turns it), so it's clicked and grabbed where it shows.
function turnedBox(box, me, rig, X, Y) {
  const ox = Math.round(X), oy = Math.round(Y), s = me.s, cos = Math.cos(me.rot), sin = Math.sin(me.rot);
  const py = me.rotAround === 'top' ? oy + rig.top * s : me.rotAround === 'center' ? oy + (rig.top + rig.bottom + 1) * s / 2 : oy;
  const xs = [], ys = [];
  for (const x of [box.l, box.r]) for (const y of [box.t, box.b]) {
    xs.push(ox + (x - ox) * cos - (y - py) * sin); ys.push(py + (x - ox) * sin + (y - py) * cos);
  }
  return { l: Math.min(...xs), r: Math.max(...xs), t: Math.min(...ys), b: Math.max(...ys) };
}

// Paints the layers in order: pixels, and block limbs turned to an angle off the pixel grid.
// (x, y) is the companion's (0, 0).
function paintLayers(ctx, layers, x, y, s, now, look, tint) {
  for (const layer of layers) {
    if (layer.px) blit(layer.px, x, y, s, now, look, tint, ctx);
    else drawLimb(ctx, layer.limb, x, y, s, now, look, tint);
  }
}

// A limb is drawn upright into a small spare canvas, then placed turned around its joint, so it
// keeps its exact shape at any angle, like a Minecraft arm or leg.
const limbCanvas = document.createElement('canvas'), limbCtx = limbCanvas.getContext('2d');
function drawLimb(ctx, limb, x, y, s, now, look, tint) {
  const dpr = window.devicePixelRatio || 1, w = limb.w * s, h = limb.n * s;
  limbCanvas.width = Math.ceil(w * dpr); limbCanvas.height = Math.ceil(h * dpr);
  limbCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
  limbCtx.clearRect(0, 0, w, h);
  blit(limb.px, 0, 0, s, now, look, tint, limbCtx);
  ctx.save();
  ctx.translate(Math.round(x) + limb.pivot[0] * s, Math.round(y) + limb.pivot[1] * s);
  ctx.rotate(limb.angle);
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(limbCanvas, limb.ox * s, limb.oy * s, w, h);
  ctx.restore();
}

// Draw the pixels upright into a spare canvas, then place that turned: the body's edges stay
// straight lines instead of breaking into steps.
function drawTurned(layers, X, Y, s, now, look, me, rig) {
  const b = bounds(layers.flatMap(l => l.px || []).concat(rig.px)), pad = 1, dpr = window.devicePixelRatio || 1;
  const w = (b.c1 - b.c0 + 1 + 2 * pad) * s, h = (b.r1 - b.r0 + 1 + 2 * pad) * s;
  off.width = Math.ceil(w * dpr); off.height = Math.ceil(h * dpr);
  offCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
  offCtx.clearRect(0, 0, w, h);
  paintLayers(offCtx, layers, -(b.c0 - pad) * s, -(b.r0 - pad) * s, s, now, look, me.tint);
  const ox = Math.round(X), oy = Math.round(Y);
  const py = me.rotAround === 'top' ? oy + rig.top * s : me.rotAround === 'center' ? oy + (rig.top + rig.bottom + 1) * s / 2 : oy;
  const ctx = env.ctx;
  ctx.save();
  ctx.translate(ox, py); ctx.rotate(me.rot); ctx.translate(-ox, -py);
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(off, ox + (b.c0 - pad) * s, oy + (b.r0 - pad) * s, w, h);
  ctx.restore();
}
